import {
  useRef,
  type AriaRole,
  type CSSProperties,
  type KeyboardEventHandler,
  type ReactNode,
  type RefObject,
} from 'react';
import { cx } from '../internal/cx';
import { Portal } from '../internal/Portal';
import { useAnchoredPosition, type Align, type Placement } from '../internal/useAnchoredPosition';
import { useDialogBehavior } from '../internal/useDialogBehavior';
import styles from './Popover.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** The element the popover is attached to (usually its trigger button). */
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  id?: string;
  role?: AriaRole;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  placement?: Placement;
  align?: Align;
  /** Make the popover at least as wide as its anchor. */
  matchWidth?: boolean;
  /** Keep Tab focus inside (for dialog-like popovers such as the date picker). */
  trapFocus?: boolean;
  /** Mark as a modal dialog (aria-modal) when trapFocus is on. */
  modal?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Move focus into the popover on open. Set false for listbox popups driven from an input. */
  focusOnOpen?: boolean;
  /** Return focus to the previously focused element on close. Default true. */
  restoreFocus?: boolean;
  className?: string;
  style?: CSSProperties;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /** Receives the panel element (e.g. for roving focus inside a menu). */
  panelRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Floating panel anchored to a trigger. Closes on Escape and outside click, and returns focus
 * to the trigger. Rendered in a portal so it is never clipped by scroll containers.
 */
export function Popover({
  open,
  onClose,
  anchorRef,
  children,
  id,
  role = 'dialog',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  placement = 'bottom',
  align = 'start',
  matchWidth = false,
  trapFocus = false,
  modal = false,
  initialFocusRef,
  focusOnOpen = true,
  restoreFocus = true,
  className,
  style,
  onKeyDown,
  panelRef: externalPanelRef,
}: PopoverProps) {
  const internalPanelRef = useRef<HTMLDivElement>(null);
  const panelRef = externalPanelRef ?? internalPanelRef;

  useAnchoredPosition({ open, anchorRef, floatingRef: panelRef, placement, align, matchWidth });
  useDialogBehavior({
    open,
    containerRef: panelRef,
    initialFocusRef,
    autoFocus: focusOnOpen,
    restoreFocus,
    onEscape: onClose,
    trapFocus,
    onOutsidePointerDown: onClose,
    ignoreRefs: [anchorRef],
  });

  if (!open) return null;

  return (
    <Portal>
      <div
        ref={panelRef}
        id={id}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-modal={role === 'dialog' && trapFocus && modal ? true : undefined}
        tabIndex={focusOnOpen ? -1 : undefined}
        className={cx(styles.popover, className)}
        style={style}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </Portal>
  );
}
